import React, { useState, useEffect, useRef } from "react";
import { chatApi } from "./chatbotApi";

// Modal di configurazione dei provider LLM (solo admin/superadmin).
// - toggle enabled per provider
// - inserimento/sostituzione API key per ogni provider (OpenAI ha già la chiave di sistema)
// - messaggio di conferma quando una chiave viene salvata
// onSaved: callback per far ricaricare il selettore modelli nella pagina chatbot.
const ProviderConfigModal = ({ onClose, onSaved }) => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingType, setSavingType] = useState("");
    const [keys, setKeys] = useState({}); // { provider_type: "chiave digitata" }
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const successTimer = useRef(null);

    useEffect(() => {
        chatApi
            .listProviders()
            .then(setProviders)
            .catch(() => setError("Impossibile caricare i provider."))
            .finally(() => setLoading(false));
        return () => {
            if (successTimer.current) clearTimeout(successTimer.current);
        };
    }, []);

    // Mostra un messaggio di conferma che sparisce da solo dopo 3s.
    const showSuccess = (msg) => {
        setSuccess(msg);
        if (successTimer.current) clearTimeout(successTimer.current);
        successTimer.current = setTimeout(() => setSuccess(""), 3000);
    };

    const patch = (pt, changes) =>
        setProviders((prev) => prev.map((p) => (p.provider_type === pt ? { ...p, ...changes } : p)));

    const toggleEnabled = async (p) => {
        setError("");
        setSavingType(p.provider_type);
        try {
            const updated = await chatApi.updateProvider(p.provider_type, { enabled: !p.enabled });
            patch(p.provider_type, { enabled: updated.enabled, has_key: updated.has_key });
            if (onSaved) onSaved();
        } catch (e) {
            setError(e.response?.data?.error || "Errore aggiornamento provider.");
        } finally {
            setSavingType("");
        }
    };

    const saveKey = async (p) => {
        const api_key = (keys[p.provider_type] || "").trim();
        if (!api_key) return;
        setError("");
        setSavingType(p.provider_type);
        try {
            const updated = await chatApi.updateProvider(p.provider_type, { api_key });
            patch(p.provider_type, { has_key: updated.has_key });
            setKeys((k) => ({ ...k, [p.provider_type]: "" }));
            showSuccess(`Chiave di ${p.label} aggiunta con successo`);
            if (onSaved) onSaved();
        } catch (e) {
            setError(e.response?.data?.error || "Errore salvataggio chiave.");
        } finally {
            setSavingType("");
        }
    };

    return (
        <div className="chatbot-modal-overlay" onClick={onClose}>
            <div className="chatbot-modal chatbot-config-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Configurazione provider LLM</h3>
                <p>Abilita i provider e inserisci le API key. La chiave di ChatGPT è già configurata a livello di sistema.</p>
                {error && <div className="chatbot-error">{error}</div>}
                {success && <div className="chatbot-success">{success}</div>}

                {loading ? (
                    <p>Caricamento…</p>
                ) : (
                    <div className="chatbot-provider-list">
                        {providers.map((p) => {
                            const busy = savingType === p.provider_type;
                            const isOpenai = p.provider_type === "openai";
                            const canEnable = isOpenai || p.has_key;
                            const noModels = (p.models || []).length === 0;
                            const keyState = isOpenai
                                ? p.has_key
                                    ? "Chiave: impostata ✓"
                                    : "Chiave: già configurata dal sistema"
                                : p.has_key
                                ? "Chiave: impostata ✓"
                                : "Chiave: mancante";
                            const placeholder = isOpenai
                                ? "Sostituisci la chiave di sistema (opzionale)…"
                                : p.has_key
                                ? "Sostituisci chiave…"
                                : "Incolla la API key…";
                            return (
                                <div key={p.provider_type} className="chatbot-provider-row">
                                    <div className="chatbot-provider-head">
                                        <div>
                                            <strong>{p.label}</strong>
                                            <span className="chatbot-provider-models">
                                                {(p.models || []).join(", ") || "nessun modello disponibile"}
                                            </span>
                                        </div>
                                        <label className="chatbot-switch">
                                            <input
                                                type="checkbox"
                                                checked={p.enabled}
                                                disabled={busy || noModels || (!p.enabled && !canEnable)}
                                                onChange={() => toggleEnabled(p)}
                                            />
                                            <span className="chatbot-switch-track" aria-hidden="true">
                                                <span className="chatbot-switch-thumb" />
                                            </span>
                                            <span className="chatbot-switch-text">
                                                {p.enabled ? "Attivo" : "Spento"}
                                            </span>
                                        </label>
                                    </div>

                                    <div className="chatbot-provider-key">
                                        <span className="chatbot-key-state">{keyState}</span>
                                        <div className="chatbot-share-row">
                                            <input
                                                type="password"
                                                placeholder={placeholder}
                                                value={keys[p.provider_type] || ""}
                                                onChange={(e) =>
                                                    setKeys((k) => ({ ...k, [p.provider_type]: e.target.value }))
                                                }
                                            />
                                            <button
                                                disabled={busy || !(keys[p.provider_type] || "").trim()}
                                                onClick={() => saveKey(p)}
                                            >
                                                Salva
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="chatbot-modal-actions">
                    <button onClick={onClose}>Chiudi</button>
                </div>
            </div>
        </div>
    );
};

export default ProviderConfigModal;
